"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClaimsUser = void 0;
/* eslint-disable new-cap */
/* eslint-disable max-len */
const crypto_1 = __importDefault(require("crypto"));
const eciesjs_1 = require("eciesjs");
const sjcl_1 = require("sjcl");
const assert_1 = __importDefault(require("assert"));
const did_resolver_interface_1 = require("@ew-did-registry/did-resolver-interface");
const keys_1 = require("@ew-did-registry/keys");
const jwt_1 = require("@ew-did-registry/jwt");
const claims_1 = require("../claims");
const utils_1 = require("../utils");
const __1 = require("..");
class ClaimsUser extends claims_1.Claims {
    constructor() {
        super(...arguments);
        this.curve = sjcl_1.ecc.curves.k256;
        this.q = this.curve.r;
        this.g = this.curve.G;
        this.paranoia = 6;
    }
    /**
     *
     * Creates token with data about subject provided in claimData
     *
     * @example
     * ```typescript
     * import { ClaimsUser } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const user = new Keys();
     * const claims = new ClaimsUser(user);
     * const claimData = {
     *     name: 'John'
     * };
     * const token = await claims.createPublicClaim(claimData);
     * ```
     * @param { IClaimData } publicData
     *
     * @returns { Promise<string> }
     */
    createPublicClaim(publicData, jwtOptions = { subject: '', issuer: '' }) {
        return __awaiter(this, void 0, void 0, function* () {
            jwtOptions.subject = jwtOptions.subject || this.did;
            jwtOptions.issuer = this.did;
            const claim = {
                did: jwtOptions.subject,
                signer: this.did,
                claimData: publicData,
            };
            return this.jwt.sign(claim, Object.assign(Object.assign({}, jwtOptions), { algorithm: jwt_1.Algorithms.ES256 }));
        });
    }
    /**
     * Used by the claim subject to create token with subject encrypted
     * private data which afterwards will be sent to the issuer. Salted private
     * fields will be saved in the `saltedFields` argument
     *
     * @example
     * ```typescript
     * import { ClaimsUser } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const user = new Keys();
     * const claims = new ClaimsUser(user);
     * const claimData = {
     *     secret: '123'
     * };
     * const claim = await claims.createPrivateClaim(claimData, issuer);
     * ```
     * @param { IClaimData } publicData object with claim subject private data
     * @param { string } issuer DID
     *
     * @returns { Promise<{token: string, saltedFields:{ [key: string]: string }}> } token with private data encrypted by issuer key
     */
    createPrivateClaim(privateData, issuer, jwtOptions = { subject: '', issuer: '' }) {
        return __awaiter(this, void 0, void 0, function* () {
            jwtOptions.subject = jwtOptions.subject || this.did;
            jwtOptions.issuer = this.did;
            const saltedFields = {};
            const claim = {
                did: this.did,
                signer: this.did,
                claimData: privateData,
            };
            const issuerPK = (yield this.document.readAttribute({
                publicKey: { id: `${issuer}#${did_resolver_interface_1.KeyTags.OWNER}` },
            }, issuer)).publicKeyHex;
            Object.entries(privateData).forEach(([key, value]) => {
                const salt = crypto_1.default.randomBytes(32).toString('base64');
                const saltedValue = value + salt;
                const encryptedValue = (0, eciesjs_1.encrypt)(issuerPK, Buffer.from(saltedValue));
                claim.claimData[key] = encryptedValue.toString('hex');
                saltedFields[key] = saltedValue;
            });
            const token = yield this.jwt.sign(claim, Object.assign(Object.assign({}, jwtOptions), { algorithm: jwt_1.Algorithms.ES256 }));
            return { token, saltedFields };
        });
    }
    /**
     * Used by the claim subject based on the salted values calculated
     * when creating private claim
     *
     * @example
     * ```typescript
     * import { ClaimsUser } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const user = new Keys();
     * const claims = new ClaimsUser(user);
     * const claimUrl = 'http://example.com';
     * const saltedFields = {
     *    secret: '123abc'
     * };
     * const claim = await claims.createProofClaim(claimUrl, saltedFields);
     * ```
     * @param { string } claimUrl - url of previously saved token
     * @param { { [keys: string]: string } } saltedFields - salted private user data
     *
     * @returns { Promise<string> }
     */
    createProofClaim(claimUrl, proofData, jwtOptions = { subject: '', issuer: '' }) {
        return __awaiter(this, void 0, void 0, function* () {
            jwtOptions.subject = jwtOptions.subject || this.did;
            jwtOptions.issuer = this.did;
            const claim = {
                did: this.did,
                signer: this.did,
                claimUrl,
                proofData,
            };
            Object.entries(proofData).forEach(([key, field]) => {
                if (field.encrypted) {
                    const k = sjcl_1.bn.random(this.q, this.paranoia);
                    const h = this.g.mult(k);
                    const hashedField = crypto_1.default
                        .createHash('sha256')
                        .update(field.value)
                        .digest('hex');
                    const a = new sjcl_1.bn(hashedField);
                    const PK = this.g.mult(a);
                    const c = sjcl_1.bn.fromBits(sjcl_1.hash.sha256.hash(this.g.x.toBits().concat(h.toBits()).concat(PK.toBits())));
                    const ca = c.mul(a).mod(this.q);
                    const s = ca.add(k).mod(this.q);
                    claim.proofData[key] = {
                        value: { h: h.toBits(), s: s.toBits() },
                        encrypted: true,
                    };
                }
                else {
                    claim.proofData[key] = {
                        value: field.value,
                        encrypted: false,
                    };
                }
            });
            return this.jwt.sign(claim, Object.assign(Object.assign({}, jwtOptions), { algorithm: jwt_1.Algorithms.ES256 }));
        });
    }
    /**
     * Verifies that content of issued and requested tokens is the same.
     * This performed before issued token is published
     *
     * @example
     * ```typescript
     * import { ClaimsUser } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const user = new Keys();
     * const claims = new UserClaims(user);
     * const verified = await claims.verifyClaimContent(issuedToken);
     * ```
     * @param { string } token - issued token
     * @throws if the proof failed
     */
    verifyClaimContent(token, verifyData) {
        const claim = this.jwt.decode(token);
        assert_1.default.deepStrictEqual(claim.claimData, verifyData, "Token payload doesn't match user data");
    }
    /**
     * Verifies token with private data received from issuer
     *
     * @example
     * ```typescript
     * import { ClaimsUser } from '@ew-did-registry/claims';
     * import { Keys } from '@ew-did-registry/keys';
     *
     * const user = new Keys();
     * const claims = new UserClaims(user);
     * const verified = await claims.verifyPrivateToken(issuedToken);
     * ```
     * @param { string } token - issued token
     * @returns {Promise<string>}
     * @throw if the proof failed
     */
    verifyPrivateClaim(token, saltedFields) {
        return __awaiter(this, void 0, void 0, function* () {
            const claim = this.jwt.decode(token);
            const issuer = claim.iss;
            const proofVerifier = new __1.ProofVerifier(yield this.document.read(issuer));
            if (!(yield proofVerifier.verifyAssertionProof(token))) {
                throw new Error('Invalid signature');
            }
            // eslint-disable-next-line no-restricted-syntax
            for (const [key, value] of Object.entries(saltedFields)) {
                const fieldHash = crypto_1.default.createHash('sha256').update(value).digest('hex');
                const PK = this.g.mult(new sjcl_1.bn(fieldHash));
                if (!sjcl_1.bitArray.equal(claim.claimData[key], PK.toBits())) {
                    throw new Error("Issued claim data doesn't match user data");
                }
            }
            const [, , issAddress] = claim.iss.split(':');
            const issIsDelegate = yield this.document.isValidDelegate(did_resolver_interface_1.DelegateTypes.verification, claim.iss);
            if (issIsDelegate) {
                return true;
            }
            yield this.document.update(did_resolver_interface_1.DIDAttribute.Authenticate, {
                algo: keys_1.KeyType.ED25519,
                type: did_resolver_interface_1.PubKeyType.VerificationKey2018,
                encoding: did_resolver_interface_1.Encoding.HEX,
                delegate: issAddress,
            });
            return true;
        });
    }
    /**
     * Verifies content of the issued claim, issuer identity and adds claim to service endpoints
     *
     * @param issued {string} claim approved by the issuer
     * @param verifyData {object} user data that should be contained in issued claim
     *
     * @returns {string} url of the saved claim
     */
    publishPublicClaim(issued, verifyData, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            this.verifyClaimContent(issued, verifyData);
            const { signer } = this.jwt.decode(issued);
            const proofVerifier = new __1.ProofVerifier(yield this.document.read(signer));
            if (!(yield proofVerifier.verifyAssertionProof(issued))) {
                throw new Error('User signature not valid');
            }
            return this.addClaimToServiceEndpoints(issued, opts);
        });
    }
    /**
     * Verifies content of the issued claim, issuer identity and add claim to service endpoints
     *
     * @param issued {string} claim with encrypted user data approved by the issuer
     * @param saltedFields {ISaltedFields} private user data
     *
     * @returns {string} url of the saved claim
     */
    publishPrivateClaim(issued, saltedFields, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const verified = yield this.verifyPrivateClaim(issued, saltedFields);
            if (verified) {
                return this.addClaimToServiceEndpoints(issued, opts);
            }
            return '';
        });
    }
    addClaimToServiceEndpoints(claim, opts = {
        hashAlg: 'SHA256',
        createHash: utils_1.hashes.SHA256,
    }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { hashAlg, createHash } = opts;
            const url = yield this.store.save(claim);
            yield this.document.update(did_resolver_interface_1.DIDAttribute.ServicePoint, {
                type: did_resolver_interface_1.PubKeyType.VerificationKey2018,
                value: { serviceEndpoint: url, hash: createHash(claim), hashAlg },
            });
            return url;
        });
    }
}
exports.ClaimsUser = ClaimsUser;
//# sourceMappingURL=claimsUser.js.map